import { Check, Copy } from "@phosphor-icons/react";
import { forwardRef, useEffect, useRef, useState, type HTMLAttributes, type MouseEvent, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { Icon } from "../../primitives/Icon";

// Reveal affordance icon — matches the reference application's eye glyph exactly.
const EyeGlyph = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M7 4.5c1.38 0 2.5 1.12 2.5 2.5S8.38 9.5 7 9.5 4.5 8.38 4.5 7 5.62 4.5 7 4.5zm0-2C3.89 2.5 1.26 4.61.5 7c.76 2.39 3.39 4.5 6.5 4.5s5.74-2.11 6.5-4.5c-.76-2.39-3.39-4.5-6.5-4.5z"
      fill="currentColor"
    />
  </svg>
);
import { Text } from "../../primitives/Text";
import { IconButton } from "../../actions/IconButton";

export type RevealAndCopySize = "xs" | "sm" | "md";

export type RevealAndCopyProps = Omit<HTMLAttributes<HTMLDivElement>, "children" | "onCopy"> & {
  /** The sensitive value to keep masked until the user reveals it. */
  value?: string | null;
  /**
   * Optional formatted/display representation rendered ONLY when revealed (e.g. a
   * formatted phone number). The mask length still derives from `value`, and the
   * plaintext `displayValue` is never present in the DOM while masked. When
   * omitted, the revealed value falls back to `value` (unchanged behavior).
   */
  displayValue?: ReactNode;
  /** Enables the copy action after reveal. */
  allowCopy?: boolean;
  /** Initial reveal state for uncontrolled usage. */
  defaultRevealed?: boolean;
  /** Controlled reveal state. */
  revealed?: boolean;
  /** Called when the user reveals the value. */
  onRevealChange?: (revealed: boolean) => void;
  /** Called after a successful copy action. */
  onCopy?: (value: string) => void;
  /** Called when clipboard copy fails. */
  onCopyError?: (error: unknown) => void;
  /** Value written to the clipboard. Defaults to `value`. */
  copyValue?: string;
  /** Visible fallback for missing values. */
  emptyValue?: ReactNode;
  /** Character used to build the masked value. */
  maskCharacter?: string;
  /** Minimum masked length. */
  minMaskLength?: number;
  /** Stops button clicks from bubbling to parent rows/cards. */
  stopPropagation?: boolean;
  size?: RevealAndCopySize;
  revealLabel?: string;
  copyLabel?: string;
  copiedLabel?: string;
  valueClassName?: string;
  actionClassName?: string;
};

/* Exact computed results of the pre-migration tailwind-merge composition.
 * The Text/IconButton primitives keep their own classes, so every override
 * here uses `&&` to win the cascade the same way tw-merge used to drop the
 * conflicting utility. In particular, tw-merge dropped BOTH the base
 * `leading-none` and Text's `leading-7` when the size class carried a
 * font-size, so xs/sm inherit line-height and md takes text-sm's 1.25rem. */

const iconSize: Record<RevealAndCopySize, number> = {
  xs: 12,
  sm: 14,
  md: 16
};

const copyResetDelayMs = 1500;

const RevealAndCopyRoot = styled("div", {
  label: "AgenticRevealAndCopy-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { hasValue: boolean } }>(({ ownerState }) => [
  {
    display: "inline-flex",
    minWidth: "0px",
    alignItems: "center",
    verticalAlign: "middle"
  },
  ownerState.hasValue && { gap: ".375rem" }
]);

const emptyTextSize: Record<RevealAndCopySize, CSSObject> = {
  xs: { fontSize: "11px", lineHeight: "inherit" },
  sm: { fontSize: "12px", lineHeight: "inherit" },
  md: { fontSize: ".875rem", lineHeight: "1.25rem" }
};

const RevealAndCopyEmptyText = styled(Text, {
  label: "AgenticRevealAndCopy-emptyText",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: RevealAndCopySize } }>(({ ownerState }) => ({
  "&&": emptyTextSize[ownerState.size]
}));

const RevealAndCopyValue = styled(Text, {
  label: "AgenticRevealAndCopy-value",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: RevealAndCopySize } }>(({ ownerState }) => ({
  "&&": {
    minWidth: "0px",
    fontWeight: 500,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    ...emptyTextSize[ownerState.size]
  }
}));

const actionSize: Record<RevealAndCopySize, CSSObject> = {
  xs: { height: ".875rem", width: ".875rem" },
  sm: { height: "14px", width: "14px" },
  md: { height: "1.5rem", width: "1.5rem" }
};

const RevealAndCopyRevealButton = styled(IconButton, {
  label: "AgenticRevealAndCopy-revealButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: RevealAndCopySize } }>(({ ownerState }) => ({
  "&&": {
    ...actionSize[ownerState.size],
    borderRadius: "var(--bfrs-radius-sm)",
    padding: 0,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))",
    "&:hover": {
      backgroundColor: "transparent",
      "--tw-text-opacity": "1",
      color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
    }
  }
}));

const RevealAndCopyCopyButton = styled(IconButton, {
  label: "AgenticRevealAndCopy-copyButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: RevealAndCopySize; copied: boolean } }>(({ ownerState }) => ({
  "&&": {
    ...actionSize[ownerState.size],
    borderRadius: "var(--bfrs-radius-sm)",
    padding: 0,
    "&:hover": {
      backgroundColor: "transparent"
    },
    ...(ownerState.copied
      ? {
          "--tw-text-opacity": "1",
          color: "hsl(var(--bfrs-color-success) / var(--tw-text-opacity, 1))"
        }
      : {
          "--tw-text-opacity": "1",
          color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))",
          "&:hover": {
            backgroundColor: "transparent",
            "--tw-text-opacity": "1",
            color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
          }
        })
  }
}));

const RevealAndCopyLiveRegion = styled("span", {
  label: "AgenticRevealAndCopy-liveRegion"
})({
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  borderWidth: 0
});

const isMissingValue = (value?: string | null) => {
  if (value === undefined || value === null) {
    return true;
  }

  const trimmed = value.trim();
  return trimmed.length === 0 || trimmed === "—";
};

const createMask = (value: string, maskCharacter: string, minMaskLength: number) => {
  const character = maskCharacter.length > 0 ? Array.from(maskCharacter)[0] : "X";
  return character.repeat(Math.max(value.length, minMaskLength));
};

export const RevealAndCopy = forwardRef<HTMLDivElement, RevealAndCopyProps>(
  (
    {
      className,
      value,
      allowCopy = true,
      defaultRevealed = false,
      revealed,
      onRevealChange,
      onCopy,
      onCopyError,
      copyValue,
      displayValue,
      emptyValue = "—",
      maskCharacter = "X",
      minMaskLength = 4,
      stopPropagation = true,
      size = "sm",
      revealLabel = "Show value",
      copyLabel = "Copy value",
      copiedLabel = "Copied",
      valueClassName,
      actionClassName,
      ...props
    },
    ref
  ) => {
    const [internalRevealed, setInternalRevealed] = useState(defaultRevealed);
    const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
    const timeoutRef = useRef<number | undefined>();
    const hasValue = !isMissingValue(value);
    const normalizedValue = value ?? "";
    const isRevealed = revealed ?? internalRevealed;
    // Mask length always derives from `value`. When revealed, prefer the optional
    // `displayValue` (formatted) and fall back to the raw value. While masked, only
    // the mask is rendered — `displayValue` is never placed in the DOM.
    const shownValue = isRevealed
      ? displayValue ?? normalizedValue
      : createMask(normalizedValue, maskCharacter, minMaskLength);

    useEffect(() => {
      return () => {
        if (timeoutRef.current !== undefined) {
          window.clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    const revealValue = (event: MouseEvent<HTMLButtonElement>) => {
      if (stopPropagation) {
        event.stopPropagation();
      }

      if (revealed === undefined) {
        setInternalRevealed(true);
      }

      onRevealChange?.(true);
    };

    const handleCopy = async (event: MouseEvent<HTMLButtonElement>) => {
      if (stopPropagation) {
        event.stopPropagation();
      }

      const textToCopy = copyValue ?? normalizedValue;

      try {
        await globalThis.navigator.clipboard.writeText(textToCopy);
        setCopyState("copied");
        onCopy?.(textToCopy);

        if (timeoutRef.current !== undefined) {
          window.clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = window.setTimeout(() => setCopyState("idle"), copyResetDelayMs);
      } catch (error) {
        onCopyError?.(error);
      }
    };

    if (!hasValue) {
      return (
        <RevealAndCopyRoot ref={ref} ownerState={{ hasValue: false }} className={className} {...props}>
          <RevealAndCopyEmptyText as="span" tone="muted" ownerState={{ size }} className={valueClassName}>
            {emptyValue}
          </RevealAndCopyEmptyText>
        </RevealAndCopyRoot>
      );
    }

    return (
      <RevealAndCopyRoot ref={ref} ownerState={{ hasValue: true }} className={className} {...props}>
        <RevealAndCopyValue as="span" ownerState={{ size }} className={valueClassName}>
          {shownValue}
        </RevealAndCopyValue>
        {!isRevealed && (
          <RevealAndCopyRevealButton
            type="button"
            aria-label={revealLabel}
            title={revealLabel}
            variant="ghost"
            size="sm"
            ownerState={{ size }}
            className={actionClassName}
            icon={<EyeGlyph size={iconSize[size]} />}
            onClick={revealValue}
          />
        )}
        {isRevealed && allowCopy && (
          <>
            <RevealAndCopyCopyButton
              type="button"
              aria-label={copyState === "copied" ? copiedLabel : copyLabel}
              title={copyState === "copied" ? copiedLabel : copyLabel}
              variant="ghost"
              size="sm"
              ownerState={{ size, copied: copyState === "copied" }}
              className={actionClassName}
              icon={<Icon icon={copyState === "copied" ? Check : Copy} size={iconSize[size]} weight="bold" />}
              onClick={handleCopy}
            />
            <RevealAndCopyLiveRegion aria-live="polite">
              {copyState === "copied" ? copiedLabel : ""}
            </RevealAndCopyLiveRegion>
          </>
        )}
      </RevealAndCopyRoot>
    );
  }
);

RevealAndCopy.displayName = "RevealAndCopy";
